/**
 * LookupField — renders a single CRM field in view or edit mode.
 * Supports different input types based on field.dataType.
 */
import { useState, useRef, useEffect } from 'react';
import { Check, X, Pencil, Loader2, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Select } from '@/components/ui/select';
import type { CRMField } from '@/types/saas';

interface LookupFieldProps {
  field: CRMField;
  value: unknown;
  editing: boolean;
  saving: boolean;
  editValue: unknown;
  onStartEdit: () => void;
  onChange: (value: unknown) => void;
  onSave: () => void;
  onCancel: () => void;
}

/** Data types that render as a simple text input */
const TEXT_TYPES = new Set([
  'text', 'phone', 'email', 'website', 'bigint', 'integer', 'autonumber', 'string',
]);

/** Data types that render as number input */
const NUMBER_TYPES = new Set(['number', 'currency', 'double']);

/** Data types that are always read-only (lookup references) */
const READONLY_TYPES = new Set(['lookup', 'ownerlookup', 'profileimage', 'formula', 'multiselectlookup']);

export function LookupField({
  field,
  value,
  editing,
  saving,
  editValue,
  onStartEdit,
  onChange,
  onSave,
  onCancel,
}: LookupFieldProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [hovered, setHovered] = useState(false);

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const isReadOnly = field.readOnly || READONLY_TYPES.has(field.dataType);
  const displayValue = formatDisplayValue(value, field);

  // Hide read-only rows whose value can't render to anything useful — covers
  // unrecognized object shapes (formatDisplayValue returns '') and other
  // empty-after-formatting cases that would otherwise show as gray "Empty"
  // clutter in the record view. Editable empty fields stay visible so the
  // user can click to add a value.
  if (!editing && !displayValue && isReadOnly) {
    return null;
  }

  // Handle keyboard shortcuts in edit mode
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (editing) {
    return (
      <div data-testid="lookup-field-edit" className="group py-1.5 px-2 rounded-md bg-primary/5 border border-primary/20">
        <label className="text-[10px] font-medium text-primary/70 uppercase tracking-wide">
          {field.displayLabel}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        <div className="mt-1 flex items-start gap-1.5">
          <div className="flex-1 min-w-0">
            {renderEditInput(field, editValue, onChange, handleKeyDown, inputRef)}
          </div>
          <div className="flex items-center gap-0.5 shrink-0 pt-0.5">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="p-1 rounded hover:bg-green-500/10 text-green-600 transition-colors disabled:opacity-50"
              title="Save (Enter)"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors disabled:opacity-50"
              title="Cancel (Esc)"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="lookup-field-view"
      className={cn(
        'group py-1.5 px-2 rounded-md transition-colors',
        !isReadOnly && 'hover:bg-muted/50 cursor-pointer'
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={!isReadOnly ? onStartEdit : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wide">
            {field.displayLabel}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </p>
          <p
            className={cn(
              'text-sm mt-0.5 break-words',
              displayValue ? 'text-foreground' : 'text-muted-foreground/40 italic'
            )}
          >
            {displayValue || 'Empty'}
          </p>
        </div>
        {!isReadOnly && hovered && (
          <Pencil className="h-3 w-3 text-muted-foreground/50 shrink-0 mt-3" />
        )}
      </div>
    </div>
  );
}

/**
 * Renders a form field for creating a new CRM record.
 * Similar to edit mode but always visible and in a form layout.
 */
export function CreateField({
  field,
  value,
  onChange,
}: {
  field: CRMField;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Allow default behavior for create fields
    if (e.key === 'Escape') {
      (e.target as HTMLElement).blur();
    }
  };

  return (
    <div className="py-1.5">
      <label className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wide">
        {field.displayLabel}
        {field.required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      <div className="mt-1">
        {renderEditInput(field, value, onChange, handleKeyDown, null)}
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function renderEditInput(
  field: CRMField,
  value: unknown,
  onChange: (value: unknown) => void,
  onKeyDown: (e: React.KeyboardEvent) => void,
  ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null> | null
) {
  const baseClass =
    'w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm ring-offset-background transition-colors focus:outline-none focus:ring-1 focus:ring-ring';

  // Social handler — array of {value, label} (multi-row picker). The CRM
  // module declares the allowed platforms via `field.socialPlatforms`.
  if (field.dataType === 'social_handler') {
    type Entry = { value: string; label: string };
    const platforms = field.socialPlatforms ?? [];
    const defaultPlatform = platforms[0]?.value ?? '';
    const entries: Entry[] = Array.isArray(value)
      ? (value as unknown[]).map((e) =>
          e && typeof e === 'object'
            ? {
                value: String((e as Record<string, unknown>).value ?? ''),
                label: String((e as Record<string, unknown>).label ?? ''),
              }
            : { value: String(e ?? ''), label: '' }
        )
      : [];
    const updateEntry = (i: number, patch: Partial<Entry>) => {
      const next = entries.map((e, idx) => (idx === i ? { ...e, ...patch } : e));
      onChange(next);
    };
    const addEntry = () => onChange([...entries, { value: '', label: defaultPlatform }]);
    const removeEntry = (i: number) => onChange(entries.filter((_, idx) => idx !== i));
    return (
      <div className="space-y-1.5">
        {entries.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No entries yet — click Add ID below.</p>
        )}
        {entries.map((entry, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              type="text"
              value={entry.value}
              onChange={(e) => updateEntry(i, { value: e.target.value })}
              onKeyDown={onKeyDown}
              placeholder="Handle or URL"
              className={cn(baseClass, 'flex-1')}
            />
            {platforms.length > 0 && (
              <Select
                value={entry.label || defaultPlatform}
                onValueChange={(v) => updateEntry(i, { label: v })}
                options={platforms}
                className="w-24 shrink-0"
              />
            )}
            <button
              type="button"
              onClick={() => removeEntry(i)}
              className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors shrink-0"
              title="Remove"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addEntry}
          className="flex items-center gap-1 text-xs text-primary hover:underline mt-1"
        >
          <Plus className="h-3.5 w-3.5" /> Add ID
        </button>
      </div>
    );
  }

  // Picklist → dropdown
  if (field.dataType === 'picklist' && field.pickListValues?.length) {
    return (
      <Select
        value={String(value ?? '')}
        onValueChange={(v) => onChange(v)}
        options={field.pickListValues.map((p) => ({
          value: p.value,
          label: p.label || p.value,
        }))}
        placeholder="Select..."
        className="w-full"
      />
    );
  }

  // Boolean → checkbox
  if (field.dataType === 'boolean') {
    return (
      <label className="flex items-center gap-2 cursor-pointer py-1">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
        />
        <span className="text-sm text-foreground">{value ? 'Yes' : 'No'}</span>
      </label>
    );
  }

  // Date
  if (field.dataType === 'date') {
    return (
      <input
        ref={ref as React.RefObject<HTMLInputElement>}
        type="date"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className={baseClass}
      />
    );
  }

  // Datetime
  if (field.dataType === 'datetime') {
    return (
      <input
        ref={ref as React.RefObject<HTMLInputElement>}
        type="datetime-local"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className={baseClass}
      />
    );
  }

  // Textarea
  if (field.dataType === 'textarea') {
    return (
      <textarea
        ref={ref as React.RefObject<HTMLTextAreaElement>}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        rows={3}
        className={cn(baseClass, 'resize-y min-h-[60px]')}
      />
    );
  }

  // Number types
  if (NUMBER_TYPES.has(field.dataType)) {
    return (
      <input
        ref={ref as React.RefObject<HTMLInputElement>}
        type="number"
        value={value != null ? String(value) : ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        onKeyDown={onKeyDown}
        step={field.dataType === 'double' || field.dataType === 'currency' ? '0.01' : '1'}
        className={baseClass}
      />
    );
  }

  // Default: text input (covers text, phone, email, website, etc.)
  return (
    <input
      ref={ref as React.RefObject<HTMLInputElement>}
      type={field.dataType === 'email' ? 'email' : field.dataType === 'website' ? 'url' : 'text'}
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      className={baseClass}
    />
  );
}

/** Format a CRM field value for display */
function formatDisplayValue(value: unknown, field: CRMField): string {
  if (value == null || value === '') return '';

  // Lookup fields store objects like { id, name }
  if (field.dataType === 'lookup' || field.dataType === 'ownerlookup') {
    if (typeof value === 'object' && value !== null && 'name' in value) {
      return String((value as { name: string }).name);
    }
  }

  // Boolean
  if (field.dataType === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Currency — prepend symbol if not already present
  if (field.dataType === 'currency' && typeof value === 'number') {
    return `$${value.toFixed(2)}`;
  }

  // Date — readable format
  if (field.dataType === 'date') {
    const d = new Date(String(value));
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }

  // Datetime — readable format with time
  if (field.dataType === 'datetime') {
    const d = new Date(String(value));
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
        + ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
  }

  // Picklist — show label if available
  if (field.dataType === 'picklist' && field.pickListValues) {
    const match = field.pickListValues.find((p) => p.value === String(value));
    if (match) return match.label || match.value;
  }

  // Social handles → "value (Platform), …" with title-case labels.
  if (field.dataType === 'social_handler' && Array.isArray(value)) {
    const labelMap = new Map((field.socialPlatforms ?? []).map((p) => [p.value, p.label]));
    return (value as unknown[])
      .map((e) => {
        if (e && typeof e === 'object') {
          const o = e as Record<string, unknown>;
          const v = String(o.value ?? '');
          const rawLabel = String(o.label ?? '').toLowerCase();
          const display = labelMap.get(rawLabel) || rawLabel;
          return display && v ? `${v} (${display})` : v || display;
        }
        return String(e ?? '');
      })
      .filter(Boolean)
      .join(', ');
  }

  // Generic object fallback — pull a sensible display string out of common
  // CRM shapes (lookup-style {name}/{label}, value containers) instead of
  // letting `String()` render "[object Object]".
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return value
        .map((e) => (e && typeof e === 'object' ? formatDisplayValue(e, field) : String(e ?? '')))
        .filter(Boolean)
        .join(', ');
    }
    const o = value as Record<string, unknown>;
    if (typeof o.name === 'string') return o.name;
    if (typeof o.display_value === 'string') return o.display_value;
    if (typeof o.label === 'string') return o.label;
    if (typeof o.value === 'string' || typeof o.value === 'number') return String(o.value);
    return ''; // unknown object shape — hide rather than show "[object Object]"
  }

  return String(value);
}
